import BaseFormatter from './base'
import { SignalVerdict, ExtractedFile } from '../../types'
import {
  AetherPipelineInfo,
  AetherPipelineStep,
  ThreatReputationStepInfo,
  DynamicAnalysisStepInfo,
  ThreatScoringStepInfo,
  ThreatHuntingStepInfo,
  VerdictCount,
} from '../../features/overview-report/types'
import { getOrderedVerdicts, getOrderedThreatVerdicts, getThreatVerdicts } from '../../helpers/signal-type'
import { productFeaturesObject, ProductFeature } from 'app/modules/shared'

export class AetherSummaryFormatter extends BaseFormatter {
  static reportSimilarityVerdictThreshold: number = 0

  format(data: { [key: string]: any }, report: { [key: string]: any }): void {
    const aetherPipeline = this.gatherAetherPipelineInfo(report)
    if (aetherPipeline && report.overview) {
      report.overview.aetherPipeline = aetherPipeline
    }
  }

  // Gather all info needed for Aether Pipeline
  gatherAetherPipelineInfo(report: { [key: string]: any }): AetherPipelineInfo | null {
    if (!productFeaturesObject.hasFeature(ProductFeature.Aether)) {
      return null
    }

    const result: AetherPipelineInfo = {
      [AetherPipelineStep.ThreatReputation]: this.gatherThreatReputationInfo(report),
      [AetherPipelineStep.DynamicAnalysis]: this.gatherDynamicAnalysisInfo(report),
      [AetherPipelineStep.ThreatScoring]: this.gatherThreatScoringInfo(report),
      [AetherPipelineStep.ThreatHunting]: this.gatherThreatHuntingInfo(report),
    }

    return result
  }

  // Aether Pipeline step 1: ThreatReputation - highest verdict IOCs count
  gatherThreatReputationInfo(report: { [key: string]: any }): ThreatReputationStepInfo {
    const iocsByVerdict: VerdictCount = {}
    const { mainFile, ...extractedFiles } = report.ioc?.data ?? {}

    if (mainFile) {
      this.countIOCsByVerdict(mainFile, iocsByVerdict)
    }

    for (const fileCategory in extractedFiles) {
      const filesInCategory = extractedFiles[fileCategory]
      for (const fileHash in filesInCategory) {
        this.countIOCsByVerdict(filesInCategory[fileHash], iocsByVerdict)
      }
    }

    const totalIocs = Object.values(iocsByVerdict).reduce((sum, count) => sum + count, 0)
    const riskyIocs = getThreatVerdicts().reduce((sum, verdict) => sum + (iocsByVerdict[verdict] ?? 0), 0)

    let verdict: SignalVerdict = SignalVerdict.Benign
    let iocsCount = totalIocs

    if (riskyIocs > 0) {
      const threatVerdictOrder = getOrderedThreatVerdicts()
      const highestThreatVerdict = threatVerdictOrder.find((v) => (iocsByVerdict[v] ?? 0) > 0)

      if (highestThreatVerdict) {
        verdict = highestThreatVerdict
        iocsCount = iocsByVerdict[highestThreatVerdict]!
      }
    }

    return {
      iocsCount,
      verdict,
    }
  }

  // Count IOCs by verdict and add to accumulator
  countIOCsByVerdict(iocTypeGroups: { [key: string]: any }, accumulator: VerdictCount): void {
    for (const iocType in iocTypeGroups) {
      const originGroups = iocTypeGroups[iocType]

      if (!Array.isArray(originGroups)) {
        continue
      }

      for (const originGroup of originGroups) {
        if (!originGroup.references || !Array.isArray(originGroup.references)) {
          continue
        }

        for (const ioc of originGroup.references) {
          let verdict: SignalVerdict = (ioc.verdict as SignalVerdict) ?? SignalVerdict.Unknown

          if (!verdict || verdict === SignalVerdict.Unknown) {
            verdict = this.getHighestOsintVerdict(ioc.osintVerdicts ?? []) ?? SignalVerdict.Unknown
          }

          accumulator[verdict] = (accumulator[verdict] || 0) + 1
        }
      }
    }
  }

  // Get the highest severity verdict from osintVerdicts array
  getHighestOsintVerdict(osintVerdicts: any[]): SignalVerdict | null {
    if (!osintVerdicts || !Array.isArray(osintVerdicts) || osintVerdicts.length === 0) {
      return null
    }

    const verdictOrder = getOrderedVerdicts()

    for (const targetVerdict of verdictOrder) {
      const found = osintVerdicts.find((osintVerdict) => osintVerdict?.verdict?.code === targetVerdict)

      if (found) {
        return targetVerdict
      }
    }

    return null
  }

  // Aether Pipeline step 2: DynamicAnalysis - emulation related threat indicators and extracted files
  gatherDynamicAnalysisInfo(report: { [key: string]: any }): DynamicAnalysisStepInfo {
    const threatIndicators = report.threatIndicators?.groups ?? {}
    const threatIndicatorsByVerdict: VerdictCount = {}

    Object.entries(threatIndicators).forEach(([verdict, groups]) => {
      if (groups && Array.isArray(groups)) {
        threatIndicatorsByVerdict[verdict as SignalVerdict] = groups.length
      }
    })

    const { count: threatIndicatorsCount, verdict: threatIndicatorsVerdict } =
      this.calculateHighestVerdictCount(threatIndicatorsByVerdict)

    const extractedFilesByVerdict: VerdictCount = {}
    const files = report.extracted_files?.files

    if (files) {
      for (const type in files) {
        if (Array.isArray(files[type])) {
          files[type].forEach((file: ExtractedFile) => {
            const verdict = (file.verdict?.verdict as SignalVerdict) ?? SignalVerdict.Unknown
            extractedFilesByVerdict[verdict] = (extractedFilesByVerdict[verdict] ?? 0) + 1
          })
        }
      }
    }

    const totalExtractedFiles = Object.values(extractedFilesByVerdict).reduce(
      (sum: number, count: number) => sum + count,
      0
    )

    const riskyExtractedFiles = getThreatVerdicts().reduce(
      (sum: number, verdict: SignalVerdict) => sum + (extractedFilesByVerdict[verdict] ?? 0),
      0
    )

    let extractedFilesVerdict: SignalVerdict = SignalVerdict.Benign

    if (riskyExtractedFiles > 0) {
      const threatVerdictOrder = getOrderedThreatVerdicts()
      const highestThreatVerdict = threatVerdictOrder.find((v) => (extractedFilesByVerdict[v] ?? 0) > 0)

      if (highestThreatVerdict) {
        extractedFilesVerdict = highestThreatVerdict
      }
    }

    return {
      threatIndicatorsCount,
      threatIndicatorsVerdict,
      totalExtractedFilesCount: totalExtractedFiles,
      extractedFilesVerdict,
    }
  }

  // Helper method to calculate highest risky verdict count
  private calculateHighestVerdictCount(verdictCounts: VerdictCount): { count: number; verdict: SignalVerdict } {
    const totalCount = Object.values(verdictCounts).reduce((sum, count) => sum + count, 0)
    const riskyCount = getThreatVerdicts().reduce((sum: number, v: SignalVerdict) => sum + (verdictCounts[v] ?? 0), 0)

    if (riskyCount === 0) {
      return { count: totalCount, verdict: SignalVerdict.Benign }
    }

    const threatVerdictOrder = getOrderedThreatVerdicts()
    const highestThreatVerdict = threatVerdictOrder.find((v) => (verdictCounts[v] ?? 0) > 0)

    return {
      count: verdictCounts[highestThreatVerdict!] ?? 0,
      verdict: highestThreatVerdict ?? SignalVerdict.Benign,
    }
  }

  // Aether Pipeline step 3: ThreatScoring - final report verdict
  gatherThreatScoringInfo(report: { [key: string]: any }): ThreatScoringStepInfo {
    const verdict = (report.verdict?.name as SignalVerdict) ?? null

    return {
      verdict: verdict,
    }
  }

  // Aether Pipeline step 4: ThreatHunting - similarity count by verdict
  gatherThreatHuntingInfo(report: { [key: string]: any }): ThreatHuntingStepInfo {
    const similarityResults = report.similaritySearchResults ?? []
    const similarityByVerdict: VerdictCount = {}

    const filteredResults = similarityResults.filter(
      (result: any) => (result?.overall_similarity ?? 0) >= AetherSummaryFormatter.reportSimilarityVerdictThreshold
    )

    filteredResults.forEach((result: any) => {
      const verdict = (result?.details?.verdict as SignalVerdict) ?? SignalVerdict.Unknown
      similarityByVerdict[verdict] = (similarityByVerdict[verdict] ?? 0) + 1
    })

    const totalSimilarity = Object.values(similarityByVerdict).reduce((sum, count) => sum + count, 0)
    const riskySimilarity = getThreatVerdicts().reduce((sum, verdict) => sum + (similarityByVerdict[verdict] ?? 0), 0)

    let verdict: SignalVerdict = SignalVerdict.Benign
    let similarReportsCount = totalSimilarity

    if (riskySimilarity > 0) {
      const threatVerdictOrder = getOrderedThreatVerdicts()
      const highestThreatVerdict = threatVerdictOrder.find((v) => (similarityByVerdict[v] ?? 0) > 0)

      if (highestThreatVerdict) {
        verdict = highestThreatVerdict
        similarReportsCount = similarityByVerdict[highestThreatVerdict]!
      }
    }

    return {
      similarReportsCount,
      verdict,
    }
  }
}
